import React from "react";
import {
  Badge,
  Box,
  Card,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Progress,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";

const getItemLabel = (item) =>
  item?.label || item?.title || item?.text || "Untitled item";

const groupItems = (items) =>
  items.reduce((acc, item) => {
    const category = item.category || "General";
    acc[category] = acc[category] || [];
    acc[category].push(item);
    return acc;
  }, {});

const WorkbookProgressChecklist = ({
  title,
  items = [],
  progress = {},
  selectedItemId,
  loading = false,
  completedCount = 0,
  totalCount = 0,
  percentComplete = 0,
  onSelect,
  onToggle,
  readOnly = false,
  footer = null,
  emptyMessage = "No checklist items are available for this area yet.",
}) => {
  const groupedItems = groupItems(items);

  return (
    <Card
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.200"
      position={{ base: "static", lg: "sticky" }}
      top="24px"
      bg="white"
      boxShadow="sm"
    >
      <CardBody>
        <VStack align="stretch" spacing={5}>
          <Box>
            <Flex justify="space-between" align="start" gap={3} mb={3}>
              <Box>
                <Heading size="sm">{title}</Heading>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  {completedCount} of {totalCount} complete
                </Text>
              </Box>
              <Badge colorScheme={percentComplete === 100 ? "green" : "orange"}>
                {percentComplete}%
              </Badge>
            </Flex>
            <Progress
              value={percentComplete}
              size="sm"
              colorScheme={percentComplete === 100 ? "green" : "orange"}
              borderRadius="full"
            />
          </Box>

          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.600" fontSize="sm">
              {emptyMessage}
            </Text>
          ) : (
            <VStack align="stretch" spacing={5}>
              {Object.entries(groupedItems).map(([category, categoryItems]) => (
                <Box key={category}>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    mb={2}
                  >
                    {category}
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {categoryItems.map((item, index) => {
                      const itemId = item.id || `${category}-${index}`;
                      const isCompleted = progress[item.id] ?? item.completed;
                      const isSelected = String(selectedItemId) === String(itemId);

                      return (
                        <Box
                          key={itemId}
                          as="button"
                          type="button"
                          onClick={() => onSelect(item)}
                          textAlign="left"
                          borderWidth="1px"
                          borderColor={isSelected ? "orange.300" : "gray.200"}
                          bg={
                            isSelected
                              ? "orange.50"
                              : isCompleted
                              ? "green.50"
                              : "white"
                          }
                          borderRadius="lg"
                          px={3}
                          py={3}
                          boxShadow={isSelected ? "sm" : "none"}
                          _hover={{ borderColor: "orange.300", bg: "orange.50" }}
                        >
                          <HStack align="flex-start" spacing={3}>
                            <Checkbox
                              colorScheme={isCompleted ? "green" : "orange"}
                              isChecked={Boolean(isCompleted)}
                              isDisabled={readOnly}
                              onChange={(event) => {
                                event.stopPropagation();
                                onToggle(item.id, event.target.checked);
                              }}
                              onClick={(event) => event.stopPropagation()}
                              mt="2px"
                              flexShrink={0}
                            />
                            <Box minW={0}>
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={isCompleted ? "gray.600" : "gray.900"}
                                textDecoration={isCompleted ? "line-through" : "none"}
                                wordBreak="break-word"
                              >
                                {getItemLabel(item)}
                              </Text>
                              {isCompleted ? (
                                <Text fontSize="xs" color="green.700" mt={1}>
                                  Completed
                                </Text>
                              ) : null}
                            </Box>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}

          {footer}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default WorkbookProgressChecklist;
